// LLMCostByModelTile.js — 30-day cost breakdown by model name.
// Becomes useful once Haiku routing experiments start; until then it just
// shows one bar for the active model.

import React from 'react';
import { Card, CardContent, Typography, Box, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useLLMByModel, toCSV, downloadCSV } from '../../../hooks/useAdminMetrics';

const formatUSD = (n) => `$${n.toFixed(2)}`;

const LLMCostByModelTile = ({ days = 30 }) => {
  const { rows, loading, error } = useLLMByModel(days);

  const handleCSV = () => {
    downloadCSV(
      `llm-cost-by-model-${days}d.csv`,
      toCSV(rows, [
        { key: 'name', header: 'model' },
        { key: 'calls', header: 'calls' },
        { key: 'cost_usd', header: 'cost_usd' },
      ]),
    );
  };

  if (error) {
    return (
      <Card><CardContent>
        <Typography color="error">Cost-by-model unavailable: {error.message}</Typography>
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" gutterBottom>LLM Cost by Model ({days}d)</Typography>
          <MuiTooltip title="Download CSV">
            <span>
              <IconButton size="small" onClick={handleCSV} disabled={loading || rows.length === 0}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </MuiTooltip>
        </Box>
        <Box sx={{ width: '100%', height: 240 }}>
          {loading ? (
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No data yet.</Typography>
          ) : (
            <ResponsiveContainer>
              <BarChart data={rows} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={formatUSD} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatUSD(v)} />
                <Bar dataKey="cost_usd" fill="#7b1fa2" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          LLM cost split by Claude model used. Becomes interesting once routing experiments mix Sonnet and Haiku.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default LLMCostByModelTile;
